import { useState, useEffect, useCallback } from 'react';
import SafetyCategory from '../components/safetyPrintComponents/SafetyCategory';
import SafetyCategoryNoHistory from '../components/safetyPrintComponents/SafetyCategoryNoHistory';
import { Grid, Button } from '@material-ui/core';
import SingleWorkOrderTable from '../components/safetyPrintComponents/SingleWorkOrderTable';
import SafetyPrintHeader from '../components/safetyPrintComponents/SafetyPrintHeader';
import SafetyNotesAdditional from '../components/safetyPrintComponents/SafetyNotesAdditional';
import { getPrintLogReport, generatePrintLogReport, modifiedWorkOrder, fetchPreviousVisits, hasAnyRedSliderSelected, isAllowedToPrintAndSendEmail, verifyContent, upsertDraftLogReport } from '../services/safetyCheckReportService';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import useShowMessages from "../hooks/useShowMessages";
import useLoading from '../hooks/useLoading';
import useWorkOrders from "../hooks/useWorkOrders";
import VoidedWO from "../components/VoidedWO";
import { sendReportToSFMC } from "../services/sfmcReportService";
import { getCustomerInformation } from "../services/customerService";
import useStore from '../hooks/useStore';
import useLicensePlates from '../hooks/useLicensePlates';
import SavedExitModal from '../components/SavedExitModal';
import { getDraftLogReport } from '../services/safetyCheckReportService';

const batteryCategory = {
    name: 'Battery',
    subCategories: [{ name: "Visual Battery Inspection and Load Test", value: "" }],
    notes: '',
    previousVisits: []
}
const defaultPrint = {
    workOrderId: '',
    customerId: '',
    odsCustomerId: '',
    printedDate: '',
    storeId: '',
    phone: '',
    ptscFlag: '',
    vehicleDetails: {
        make: '',
        model: '',
        year: '',
        state: '',
        vehicleId: '',
        license: ''
    },
    categories: [{
        name: 'Non-Winter Tires',
        subCategories: [{ name: "Visual Tire Inspection", value: "" }],
        notes: '',
        previousVisits: []
    }, {
        name: 'Winter Tires',
        subCategories: [{ name: "Visual Tire Inspection", value: "" }],
        notes: '',
        previousVisits: []
    }, {
        name: 'Alignment',
        subCategories: [{ name: "Visual Alignment Inspection", value: "" }],
        notes: '',
        previousVisits: []
    }, {
        name: 'Steering and Suspension Components',
        subCategories: [{ name: "Visual Inspection of Front End Components", value: "" },
        { name: "Visual Inspection of Shocks and/or Struts", value: "" }],
        notes: '',
        previousVisits: []
    }, {
        name: 'Brakes',
        subCategories: [{ name: "Visual Inspection of Front Brakes", value: "" },
        { name: "Visual Inspection of Rear Brakes", value: "" }],
        notes: '',
        previousVisits: []
    }
    ],
    additionalNotes: ''
}

const SafetyPrint = (props) => {
    const { selectedWO, handleBack, sendEmailButton, printButton, setSendEmailButton, setPrintButton } = props;
    const [print, setPrint] = useState(null);
    const [workOrderModification, setWorkOrderModification] = useState(false);
    const [reset, setReset] = useState(false)
    const [serviceDate, setServiceDate] = useState(new Date())
    const { setIsLoading } = useLoading();
    const { showMessage } = useShowMessages();
    const { removedWO } = useWorkOrders()
    const [customerInformation, setCustomerInformation] = useState({})
    const { selectedStoreConfiguration: { isSalesForceEmailEnabled, isPreviousInspectionViewEnabled, isEmailPriorityEnabled, isStatusInspectionEnabled } } = useStore();
    const { licensePlatesToExclude } = useLicensePlates()
    const plateExcluded = licensePlatesToExclude.findIndex(obj => obj.license_plate === selectedWO.License && obj.state === selectedWO.State);
    const isLicenseExcluded = plateExcluded !== -1;
    const [previousInspection, setPreviousInspection] = useState({ categories: {}, lastVisitDate: '' });
    const [loadingVisits, setLoadingVisits] = useState(true);
    const [isPreviousInspectionLoaded, setIsPreviousInspectionLoaded] = useState(false);
    const isPreviousVisitFetchAvailable = !isLicenseExcluded && selectedWO.License && selectedWO.State;
    const [hasAnyRedValue, setHasAnyRedValue] = useState(false);
    const [wasPrintedOrEmailed, setWasPrintedOrEmailed] = useState(false);
    const [showSavedExitModal, setShowSavedExitModal] = useState(false);

    useEffect(() => {
        let isMounted = true;
        // Remove the variable to determinate who prints the report first each time the component is mounted
        sessionStorage.removeItem('isSourcePrint')

        const fetchHistoricalVisits = async (selectedWO) => {
            setLoadingVisits(true);
            if (isPreviousVisitFetchAvailable) {
                const historical = await fetchPreviousVisits(selectedWO.License, selectedWO.State, selectedWO.StoreId);
                if (isMounted) {
                    if (historical.lastVisitDate !== '') {
                        setPreviousInspection(historical);
                        setLoadingVisits(false);
                    }
                }
            }
            setIsPreviousInspectionLoaded(true);
        }

        if (isPreviousInspectionViewEnabled) {
            fetchHistoricalVisits(selectedWO);
        }

        return () => {
            isMounted = false;
        };
    }, [selectedWO]);

    const fillReportDetails = useCallback(async (oldPrint) => {
        return {
            ...oldPrint,
            workOrderId: selectedWO.WorkOrder,
            customerId: selectedWO.CustomerId,
            odsCustomerId: selectedWO.OdsCustomerId,
            storeId: selectedWO.StoreId,
            phone: selectedWO.Phone,
            ptscFlag: selectedWO.PtscFlag,
            timeExpected: selectedWO.TimeExpected,
            vehicleDetails: {
                make: selectedWO.Make,
                model: selectedWO.Model,
                year: selectedWO.Year,
                license: selectedWO.License,
                vehicleId: selectedWO.VehicleId,
                state: selectedWO.State,
            },
            categories: oldPrint.categories,
        }

    }, [selectedWO])

    const generateReport = async (printOption, print) => {
        try {
            const printVerification = verifyContent(print);
            if (printVerification.isValid) {
                setIsLoading(true);
                // Get the sessionStorage variable to determinate if the user is the first one who prints the report
                const isSourcePrint = sessionStorage.getItem('isSourcePrint') === "true"
                const printLogReport = await generatePrintLogReport(print, printOption, isSourcePrint);
                if(printLogReport?.isInvalid) {
                    // Display warning alert when the printLog report request returns a message indicating that another user is printing the same report
                    showMessage(printLogReport.message, "warning");
                    setTimeout(() => {
                        handleBack();
                    }, 5000);
                }
                selectedWO.wasPrintedOrEmailed = true;
                setIsLoading(false);
                setWorkOrderModification(false);
                setWasPrintedOrEmailed(true);
            } else {
                showMessage(printVerification.message, "error");
            }
            return printVerification;
        } catch (error) {
            console.error('Error: ', error);
            showMessage("Error generating report", "error");
        }
    }

    const printAndSendEmail = async (isPrinted, print) => {
        const generatedReport = await generateReport(isPrinted, print)
        if (generatedReport.isValid) {
            const response = await sendReportToSFMC(selectedWO.StoreId, selectedWO.WorkOrder)
            if (!isPrinted && !response.isInvalid) {
                showMessage("The Safety Status Check report was emailed to the customer", "success");
                setWorkOrderModification(false);
                setWasPrintedOrEmailed(true);
            }
        }
    }

    useEffect(() => {
        if (print) {
            const hasRedValue = hasAnyRedSliderSelected(print);
            setHasAnyRedValue(hasRedValue);
        }
    }, [print]);

    useEffect(() => {
        if (isSalesForceEmailEnabled) {
            const { canSendEmail, canPrintAndSendEmail, title } = isAllowedToPrintAndSendEmail(
                { selectedWO, customerInformation },
                { hasAnyRedValue, isEmailPriorityEnabled }
            );

            printButton.title = title;

            if (canPrintAndSendEmail) {
                printButton.onClick = async (report) => await printAndSendEmail(true, report);
            } else {
                printButton.onClick = async (report) => await generateReport(true, report);
            }

            sendEmailButton.disabled = !canSendEmail;
            sendEmailButton.onClick = async (report) => await printAndSendEmail(false, report);

            setSendEmailButton({ ...sendEmailButton });
            setPrintButton({ ...printButton });
        } else {
            printButton.onClick = async (report) => await generateReport(true, report);
            setPrintButton({ ...printButton });
        }
    }, [hasAnyRedValue, customerInformation]);

    useEffect(async () => {
        async function setData() {
            if (selectedWO) {
                if (selectedWO.wasPrintedOrEmailed) {
                    const newPrint = await getPrintLogReport(selectedWO)
                    setPrint(newPrint)
                    setServiceDate(new Date(selectedWO.ServiceDate))
                }
                else {
                    let newPrint = (JSON.parse(JSON.stringify(defaultPrint)))
                    if (selectedWO.PtscFlag) {
                        newPrint.categories.push(JSON.parse(JSON.stringify(batteryCategory)))
                    };
                    newPrint = await fillReportDetails(newPrint)
                    setPrint(newPrint)
                }
                const customer = await getCustomerInformation(selectedWO)
                setCustomerInformation(customer)
            }
            setIsLoading(false)
        }
        await setData();
    }, [reset, selectedWO, fillReportDetails, setIsLoading])

    useEffect(() => {
        async function modifyWO() {
            if (workOrderModification) {
                try {
                    await modifiedWorkOrder(selectedWO, print);
                } catch (error) {
                    console.error("Error modifying work order: ", error);
                }
            }
        }
        modifyWO()
    }, [workOrderModification, selectedWO])

    useEffect(() => {
        const fetchDraftLog = async () => {
            try {
                setIsLoading(true)
                const draftLog = await getDraftLogReport(selectedWO.WorkOrder, selectedWO.StoreId);
                setPrint(draftLog);
            } catch (error) {
                console.error("Error fetching draft log:", error);
            }
            finally {
                setIsLoading(false)
            }
        };
        if (isStatusInspectionEnabled && selectedWO.Status == "PRG") fetchDraftLog();
    }, [selectedWO]);

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const handleCategoryChange = (evt, value, subcategoryName, categoryName) => {
        const modifiedCategories = print.categories.map(category => {
            if (category.name === categoryName) {
                return {
                    ...category,
                    subCategories: category.subCategories.map(subCategory =>
                        subCategory.name === subcategoryName
                            ? { ...subCategory, value }
                            : subCategory
                    )
                };
            }
            return category;
        });

        setPrint(prevPrint => ({ ...prevPrint, categories: modifiedCategories }));
        setWorkOrderModification(true);
    }

    const handleNotesChange = (newNotes, categoryName) => {
        const modifiedPrint = print.categories.map(category => {
            if (category.name === categoryName) {
                return {
                    ...category,
                    notes: newNotes
                }
            }
            return category;
        })
        setPrint(prevprint => ({ ...prevprint, categories: modifiedPrint }))
        setWorkOrderModification(true);
    }

    const handleAdditionalNotesChange = (newAdditionalNotes) => {
        setPrint(prevprint => {
            return { ...prevprint, additionalNotes: newAdditionalNotes }
        });
        setWorkOrderModification(true);
    }

    const handleReset = () => {
        setIsLoading(true)
        setReset(!reset)
        setWorkOrderModification(false)
    }

    const handleSavedExit = async () => {
        if (isStatusInspectionEnabled && workOrderModification) {
            setShowSavedExitModal(true);
        }
        else {
            handleBack();
        }
    }

    const mapWorkOrderToTable = () => {
        const options = { month: "2-digit", day: "2-digit", year: "numeric" };
        return {
            serviceDate: serviceDate.toLocaleString("en-US", options).replace("/", "-").replace("/", "-"),
            workOrderId: selectedWO.WorkOrder,
            customer: selectedWO.Name,
            vehicle: selectedWO.YearMakeModel,
            license: selectedWO.License ? selectedWO.License : "Unknown",
            id: selectedWO.id
        };
    };
    const handleCloseModal = () => {
        setShowSavedExitModal(false);
    };
    const handleSaveAndExit = async () => {
        try {
            setIsLoading(true);
            const rawResponse = await upsertDraftLogReport(print);
            let response;
            if (rawResponse && typeof rawResponse.json === 'function') {
                response = await rawResponse.json();
            } else {
                response = rawResponse;
            }
            
            if (response && response.isInvalid) {
                showMessage(response.description, "error");
                setIsLoading(false);
                return;
            }
            showMessage("Draft log saved successfully", "success");
            setShowSavedExitModal(false);
            handleBack();
        } catch (error) {
            console.error("Error saving draft log:", error);
            showMessage("Error saving draft log", "error");
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <div className='safety-print'>
            <div className='safety-print-sticky-section'>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SingleWorkOrderTable
                            workOrder={mapWorkOrderToTable()}
                        />
                    </Grid>
                </Grid>
                <Grid container xs={12} className='mt-4' direction='row-reverse'>
                    <Grid item xs={2} className='flex justify-end'>
                        <Button startIcon={<ArrowBackIcon />} color="secondary" style={{ padding: ".5em 2em" }} variant="contained" onClick={handleSavedExit}>{isStatusInspectionEnabled && workOrderModification ? "Save & Exit" : "Back"}</Button>
                    </Grid>
                </Grid>
                <Grid container className="safety-header-grid" spacing={2}>
                    <Grid item xs={12}>
                        <SafetyPrintHeader />
                    </Grid>
                </Grid>
            </div>
            {showSavedExitModal &&
                <SavedExitModal
                    open={showSavedExitModal}
                    onClose={handleCloseModal}
                    onSaveAndExit={handleSaveAndExit}
                    onExitWithoutSaving={handleBack}
                    title="There are unsaved changes"
                    message="Do you want to save the inspection before exiting?"
                />}
            {isPreviousInspectionViewEnabled ?
                <Grid container className="safety-status-grid" spacing={2}>
                    {print && print.categories && print.categories.map((category, index) => (
                        <Grid item xs={12} key={category.name}>
                            <SafetyCategory
                                category={{ ...category, previousCategoryInspectionValue: previousInspection.categories ? previousInspection.categories[category.name] || [] : [] }}
                                index={index}
                                handleCategoryChange={handleCategoryChange}
                                handleNotesChange={handleNotesChange}
                                date={previousInspection.lastVisitDate || ''}
                                loadingVisits={loadingVisits}
                                isPreviousInspectionLoaded={isPreviousInspectionLoaded}
                            />
                        </Grid>
                    ))}
                </Grid> :
                <Grid container className="safety-status-grid" spacing={2}>
                    {print && print.categories && print.categories.map((category, index) => (
                        <Grid item xs={12} key={category.name}>
                            <SafetyCategoryNoHistory
                                category={category}
                                index={index}
                                handleCategoryChange={handleCategoryChange}
                                handleNotesChange={handleNotesChange}
                            />
                        </Grid>
                    ))}
                </Grid>}

            <Grid container className="additional-safety-notes additional-safety-notes-grid pb-8" spacing={2} xs={12} sm={12}>
                <Grid item xs={12} sm={6} className="pr-8">
                    <SafetyNotesAdditional
                        key={"SafetyNotesAdditional"}
                        handleChange={handleAdditionalNotesChange}
                        title="Additional Notes"
                        notes={print?.additionalNotes}
                        category={'additional'}
                    />
                </Grid>
                <Grid container item xs={6} sm={3} className='relative' style={{
                    display: "flex",
                    flexDirection: "column", justifyContent: "flex-end"
                }}>
                    <div className='back-reset-buttons'>
                        <Button startIcon={<ArrowBackIcon />} color="secondary" style={{ padding: ".5em 2em" }} variant="contained" onClick={handleSavedExit}>{isStatusInspectionEnabled && workOrderModification ? "Save & Exit" : "Back"}</Button>
                        <Button color="secondary" style={{ padding: ".5em 2em" }} variant="contained" onClick={handleReset} disabled={isStatusInspectionEnabled && wasPrintedOrEmailed}>Reset</Button>
                    </div>
                </Grid>
                <Grid item container xs={12} sm={3} style={{ flexDirection: "column", justifyContent: "flex-end" }}>
                    <div className='email-buttons'>
                        {isSalesForceEmailEnabled ? <Button style={{ backgroundColor: "#379811", color: "#fff", width: '15.6em' }} color="primary" variant="contained" disabled={sendEmailButton.disabled} onClick={() => sendEmailButton.onClick(print)}>{sendEmailButton.title}</Button> : <></>}
                        <Button style={{ backgroundColor: "#379811", color: "#fff", width: '15.6em', marginTop: ".8em" }} color="primary" variant="contained" onClick={() => printButton.onClick(print)}>{printButton.title}</Button>
                    </div>

                    {
                        Array.isArray(removedWO) && removedWO.includes(selectedWO.WorkOrder) ?
                            <VoidedWO {...props} /> : null}
                </Grid>
            </Grid>
        </div>
    );
}

export default SafetyPrint;